// filtri/pelle.ts — v043: la «pelle» dei filtri. Un filtro disegna una TRAMA nello spazio del viso di MediaPipe
// (un viso «srotolato» visto di fronte, vedi pelle-dati.ts) e il motore la incolla su tutto il volto: segue guance,
// fronte, mento e ogni espressione, come un trucco o una maschera. È ciò che serve per TRASFORMARE la faccia (in
// tigre, in ape, in robot…) invece di disegnarci sopra degli adesivi.
//
// - Occhi e bocca restano quelli veri: nella mappa sono dei buchi, e la zona della bocca si svuota comunque (chi
//   gioca deve poter vedere l'altro ridere).
// - Con la fusione «luce» (predefinita) i colori della trama prendono la luce e le ombre del viso vero: sembra pelle
//   dipinta, non un adesivo piatto. «pieno» = i colori come sono (maschere, robot, metallo).
// - Il bordo del viso sfuma (niente gradino tra la trama e i capelli o il collo).
import { UV_PELLE, TRIANGOLI_PELLE } from "./pelle-dati.ts";
import { LABBRA_INTERNE, OCCHIO_DESTRO, OCCHIO_SINISTRO, OVALE, P, SOPRACCIGLIO_DESTRO, SOPRACCIGLIO_SINISTRO } from "./punti.ts";
import type { ContestoFiltro, Volto } from "./tipi.ts";

export const PUNTI_PELLE = 468;

/** Un punto della trama, in pixel della trama. */
export interface PuntoTrama {
  x: number;
  y: number;
}

/** Aiuti per disegnare la trama: dove stanno gli occhi, il naso, le guance… sulla trama. */
export interface MappaPelle {
  /** Lato della trama in pixel (quadrata). */
  readonly lato: number;
  /** Il punto `i` del volto (0…467, gli stessi indici di punti.ts) sulla trama. */
  punto(i: number): PuntoTrama;
  /** Media di più punti (es. il centro di un occhio). */
  centro(indici: readonly number[]): PuntoTrama;
  /** Un percorso chiuso che passa per i punti dati, nell'ordine (per riempire o ritagliare una zona). */
  percorso(indici: readonly number[]): Path2D;
  /** Il contorno del viso (tutta la parte che si vede). */
  readonly ovale: Path2D;
  /** Punti già pronti. Occhi: il centro. Destro/sinistro della persona (destro = a sinistra della trama). */
  readonly ancore: {
    occhioDestro: PuntoTrama;
    occhioSinistro: PuntoTrama;
    sopraccDestro: PuntoTrama;
    sopraccSinistro: PuntoTrama;
    radiceNaso: PuntoTrama;
    puntaNaso: PuntoTrama;
    sottoNaso: PuntoTrama;
    bocca: PuntoTrama;
    angoloBoccaDestro: PuntoTrama;
    angoloBoccaSinistro: PuntoTrama;
    mento: PuntoTrama;
    fronte: PuntoTrama;
    fronteAlto: PuntoTrama;
    guanciaDestra: PuntoTrama;
    guanciaSinistra: PuntoTrama;
    zigomoDestro: PuntoTrama;
    zigomoSinistro: PuntoTrama;
  };
  /** Distanza tra i centri degli occhi sulla trama: l'unità per le misure (come `scala` sul volto). */
  readonly unita: number;
}

/** La pelle di un filtro (campo facoltativo `pelle` del Filtro). */
export interface PelleFiltro {
  /** Lato della trama in pixel (predefinito 512; massimo 1024). */
  lato?: number;
  /** true = la trama si ridisegna a ogni fotogramma (animata); false = una volta sola (più leggera). */
  animata?: boolean;
  /** «luce» (predefinita): i colori prendono le ombre e la luce del viso vero. «pieno»: colori così come sono. */
  fusione?: "luce" | "pieno";
  /** Quanto conta la luce del viso con «luce» (0…1, predefinito 0.85). */
  luce?: number;
  /** Sfuma il bordo del viso (predefinito sì). */
  sfumaBordo?: boolean;
  /** Sfuma anche attorno a occhi e labbra (predefinito sì): la trama non finisce di colpo sulle palpebre. */
  sfumaOcchiBocca?: boolean;
  /** Trasparenza di tutta la pelle in questo fotogramma (0…1, predefinito 1): per comparse e dissolvenze. */
  opacita?(c: ContestoFiltro): number;
  /**
   * Disegna la trama. `ctx` è grande lato×lato e arriva TRASPARENTE (si disegna solo dove serve: il resto resta il
   * viso vero). Si chiama una volta (o a ogni fotogramma se `animata`). `c.volto` può essere null (nessun volto).
   */
  disegna(ctx: CanvasRenderingContext2D, c: ContestoFiltro, mappa: MappaPelle): void;
}

/** La mappa per una trama di lato `lato` (pura: si prova in node). */
export function mappaPelle(lato: number): MappaPelle {
  const punto = (i: number): PuntoTrama => ({ x: UV_PELLE[i * 2]! * lato, y: UV_PELLE[i * 2 + 1]! * lato });
  const centro = (indici: readonly number[]): PuntoTrama => {
    let x = 0;
    let y = 0;
    for (const i of indici) {
      const p = punto(i);
      x += p.x;
      y += p.y;
    }
    return { x: x / indici.length, y: y / indici.length };
  };
  const percorso = (indici: readonly number[]): Path2D => {
    const p = new Path2D();
    indici.forEach((i, k) => {
      const q = punto(i);
      if (k === 0) p.moveTo(q.x, q.y);
      else p.lineTo(q.x, q.y);
    });
    p.closePath();
    return p;
  };
  const occhioDestro = centro(OCCHIO_DESTRO);
  const occhioSinistro = centro(OCCHIO_SINISTRO);
  return {
    lato,
    punto,
    centro,
    percorso,
    get ovale() {
      return percorso(OVALE);
    },
    ancore: {
      occhioDestro,
      occhioSinistro,
      sopraccDestro: centro(SOPRACCIGLIO_DESTRO),
      sopraccSinistro: centro(SOPRACCIGLIO_SINISTRO),
      radiceNaso: punto(P.radiceNaso),
      puntaNaso: punto(P.puntaNaso),
      sottoNaso: punto(P.sottoNaso),
      bocca: centro(LABBRA_INTERNE),
      angoloBoccaDestro: punto(P.angoloBoccaDestro),
      angoloBoccaSinistro: punto(P.angoloBoccaSinistro),
      mento: punto(P.mento),
      fronte: punto(P.fronte),
      fronteAlto: punto(P.fronteAlto),
      guanciaDestra: punto(P.guanciaDestra),
      guanciaSinistra: punto(P.guanciaSinistra),
      zigomoDestro: punto(P.zigomoDestro),
      zigomoSinistro: punto(P.zigomoSinistro),
    },
    unita: Math.hypot(occhioSinistro.x - occhioDestro.x, occhioSinistro.y - occhioDestro.y),
  };
}

/** Quanto sfumare ogni punto: x = bordo del viso, y = attorno a occhi e labbra (0 o 1). Pura. */
export function bordiPelle(): Float32Array {
  const b = new Float32Array(PUNTI_PELLE * 2);
  for (const i of OVALE) b[i * 2] = 1;
  for (const i of [...OCCHIO_DESTRO, ...OCCHIO_SINISTRO, ...LABBRA_INTERNE]) b[i * 2 + 1] = 1;
  return b;
}

const VERTICI = `#version 300 es
in vec2 a_pos;
in vec2 a_uv;
in vec2 a_bordo;
uniform vec2 u_dimensioni;
out vec2 v_uv;
out vec2 v_bordo;
out vec2 v_schermo;
void main() {
  v_uv = a_uv;
  v_bordo = a_bordo;
  v_schermo = a_pos / u_dimensioni;
  gl_Position = vec4(v_schermo.x * 2.0 - 1.0, 1.0 - v_schermo.y * 2.0, 0.0, 1.0);
}
`;

const FRAMMENTI = `#version 300 es
precision highp float;
in vec2 v_uv;
in vec2 v_bordo;
in vec2 v_schermo;
uniform sampler2D u_trama;
uniform sampler2D u_immagine;
uniform float u_luce;      // 0 = colori pieni; >0 = con la luce del viso vero
uniform vec2 u_sfuma;      // x bordo del viso, y occhi e labbra
uniform float u_opacita;
out vec4 l_colore;
float luminanza(vec3 c) { return dot(c, vec3(0.2126, 0.7152, 0.0722)); }
void main() {
  vec4 t = texture(u_trama, v_uv);
  if (t.a <= 0.001) discard;
  vec3 colore = t.rgb;
  if (u_luce > 0.0) {
    // la luce del viso vero, centrata su un grigio medio: le ombre scuriscono, i riflessi schiariscono
    float l = luminanza(texture(u_immagine, v_schermo).rgb);
    float k = clamp(0.38 + 1.25 * l, 0.25, 1.45);
    colore = mix(colore, clamp(colore * k, 0.0, 1.0), u_luce);
  }
  float sfuma = (1.0 - smoothstep(0.0, 1.0, v_bordo.x) * u_sfuma.x) * (1.0 - smoothstep(0.0, 1.0, v_bordo.y) * u_sfuma.y);
  l_colore = vec4(colore, t.a * sfuma * u_opacita);
}
`;

/**
 * Disegna la pelle sul volto con WebGL2: un canvas trasparente grande quanto il fotogramma, con la trama solo sul
 * viso. Il motore lo mette sullo strato del disegno (prima di `disegna` del filtro), dove poi si svuota la bocca.
 */
export class PassaggioPelle {
  readonly canvas: HTMLCanvasElement | OffscreenCanvas;
  private gl: WebGL2RenderingContext;
  private programma: WebGLProgram;
  private vao: WebGLVertexArrayObject;
  private bPos: WebGLBuffer;
  private tTrama: WebGLTexture;
  private tImmagine: WebGLTexture;
  private posizioni: Float32Array;
  private trama: HTMLCanvasElement | OffscreenCanvas;
  private tctx: CanvasRenderingContext2D | OffscreenCanvasRenderingContext2D;
  private mappa: MappaPelle;
  private disegnata = false;
  private loc: Record<string, WebGLUniformLocation | null> = {};
  private buffer: WebGLBuffer[] = [];
  readonly pelle: PelleFiltro;

  constructor(canvas: HTMLCanvasElement | OffscreenCanvas, pelle: PelleFiltro) {
    this.canvas = canvas;
    this.pelle = pelle;
    const gl = canvas.getContext("webgl2", { premultipliedAlpha: false, preserveDrawingBuffer: false, antialias: true, alpha: true }) as WebGL2RenderingContext | null;
    if (!gl || gl.isContextLost()) throw new Error("WebGL2 non disponibile");
    this.gl = gl;
    const compila = (tipo: number, s: string) => {
      const sh = gl.createShader(tipo)!;
      gl.shaderSource(sh, s);
      gl.compileShader(sh);
      if (!gl.getShaderParameter(sh, gl.COMPILE_STATUS)) throw new Error(`pelle: shader non compilato: ${gl.getShaderInfoLog(sh) ?? "?"}`);
      return sh;
    };
    const p = gl.createProgram()!;
    const vs = compila(gl.VERTEX_SHADER, VERTICI);
    const fs = compila(gl.FRAGMENT_SHADER, FRAMMENTI);
    gl.attachShader(p, vs);
    gl.attachShader(p, fs);
    gl.linkProgram(p);
    gl.deleteShader(vs);
    gl.deleteShader(fs);
    if (!gl.getProgramParameter(p, gl.LINK_STATUS)) throw new Error(`pelle: programma non collegato: ${gl.getProgramInfoLog(p) ?? "?"}`);
    this.programma = p;
    for (const n of ["u_dimensioni", "u_trama", "u_immagine", "u_luce", "u_sfuma", "u_opacita"]) this.loc[n] = gl.getUniformLocation(p, n);
    this.vao = gl.createVertexArray()!;
    gl.bindVertexArray(this.vao);
    const attributo = (nome: string, dati: Float32Array | null, uso: number) => {
      const b = gl.createBuffer()!;
      this.buffer.push(b);
      gl.bindBuffer(gl.ARRAY_BUFFER, b);
      if (dati) gl.bufferData(gl.ARRAY_BUFFER, dati, uso);
      const l = gl.getAttribLocation(p, nome);
      gl.enableVertexAttribArray(l);
      gl.vertexAttribPointer(l, 2, gl.FLOAT, false, 0, 0);
      return b;
    };
    this.posizioni = new Float32Array(PUNTI_PELLE * 2);
    this.bPos = attributo("a_pos", this.posizioni, gl.DYNAMIC_DRAW);
    attributo("a_uv", UV_PELLE, gl.STATIC_DRAW);
    attributo("a_bordo", bordiPelle(), gl.STATIC_DRAW);
    const bInd = gl.createBuffer()!;
    this.buffer.push(bInd);
    gl.bindBuffer(gl.ELEMENT_ARRAY_BUFFER, bInd);
    gl.bufferData(gl.ELEMENT_ARRAY_BUFFER, TRIANGOLI_PELLE, gl.STATIC_DRAW);
    gl.bindVertexArray(null);
    const nuovaTexture = () => {
      const t = gl.createTexture()!;
      gl.bindTexture(gl.TEXTURE_2D, t);
      gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR);
      gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.LINEAR);
      gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
      gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
      return t;
    };
    this.tTrama = nuovaTexture();
    this.tImmagine = nuovaTexture();
    const lato = Math.max(64, Math.min(1024, Math.round(pelle.lato ?? 512)));
    this.trama = typeof OffscreenCanvas !== "undefined" ? new OffscreenCanvas(lato, lato) : Object.assign(document.createElement("canvas"), { width: lato, height: lato });
    const tctx = this.trama.getContext("2d") as CanvasRenderingContext2D | OffscreenCanvasRenderingContext2D | null;
    if (!tctx) throw new Error("pelle: canvas 2D non disponibile");
    this.tctx = tctx;
    this.mappa = mappaPelle(lato);
  }

  /** Disegna la pelle per questo fotogramma. `sorgente` = l'immagine sotto (per la luce); false se non c'è volto. */
  esegui(sorgente: TexImageSource, c: ContestoFiltro, volto: Volto | null): boolean {
    const gl = this.gl;
    if (gl.isContextLost()) throw new Error("contesto WebGL perso");
    if (!volto || volto.punti.length < PUNTI_PELLE) return false;
    const pelle = this.pelle;
    if (!this.disegnata || pelle.animata) {
      const t = this.tctx;
      t.setTransform(1, 0, 0, 1, 0, 0);
      t.globalAlpha = 1;
      t.globalCompositeOperation = "source-over";
      t.clearRect(0, 0, this.mappa.lato, this.mappa.lato);
      t.save();
      try {
        pelle.disegna(t as CanvasRenderingContext2D, c, this.mappa);
      } finally {
        t.restore();
      }
      gl.bindTexture(gl.TEXTURE_2D, this.tTrama);
      gl.pixelStorei(gl.UNPACK_PREMULTIPLY_ALPHA_WEBGL, false);
      gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, gl.RGBA, gl.UNSIGNED_BYTE, this.trama as TexImageSource);
      this.disegnata = true;
    }
    if (this.canvas.width !== c.larghezza || this.canvas.height !== c.altezza) {
      this.canvas.width = c.larghezza;
      this.canvas.height = c.altezza;
    }
    for (let i = 0; i < PUNTI_PELLE; i++) {
      this.posizioni[i * 2] = volto.punti[i]!.x;
      this.posizioni[i * 2 + 1] = volto.punti[i]!.y;
    }
    gl.viewport(0, 0, c.larghezza, c.altezza);
    gl.clearColor(0, 0, 0, 0);
    gl.clear(gl.COLOR_BUFFER_BIT);
    gl.useProgram(this.programma);
    gl.bindVertexArray(this.vao);
    gl.bindBuffer(gl.ARRAY_BUFFER, this.bPos);
    gl.bufferSubData(gl.ARRAY_BUFFER, 0, this.posizioni);
    const luce = pelle.fusione === "pieno" ? 0 : Math.max(0, Math.min(1, pelle.luce ?? 0.85));
    gl.activeTexture(gl.TEXTURE0);
    gl.bindTexture(gl.TEXTURE_2D, this.tTrama);
    gl.uniform1i(this.loc.u_trama!, 0);
    gl.activeTexture(gl.TEXTURE1);
    gl.bindTexture(gl.TEXTURE_2D, this.tImmagine);
    if (luce > 0) gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, gl.RGBA, gl.UNSIGNED_BYTE, sorgente);
    gl.uniform1i(this.loc.u_immagine!, 1);
    gl.uniform2f(this.loc.u_dimensioni!, c.larghezza, c.altezza);
    gl.uniform1f(this.loc.u_luce!, luce);
    gl.uniform2f(this.loc.u_sfuma!, pelle.sfumaBordo === false ? 0 : 1, pelle.sfumaOcchiBocca === false ? 0 : 1);
    const op = pelle.opacita ? pelle.opacita(c) : 1;
    gl.uniform1f(this.loc.u_opacita!, Number.isFinite(op) ? Math.max(0, Math.min(1, op)) : 1);
    gl.disable(gl.BLEND); // un solo strato: i triangoli non si sovrappongono
    gl.drawElements(gl.TRIANGLES, TRIANGOLI_PELLE.length, gl.UNSIGNED_SHORT, 0);
    gl.bindVertexArray(null);
    gl.activeTexture(gl.TEXTURE0);
    return true;
  }

  libera(): void {
    const gl = this.gl;
    gl.deleteTexture(this.tTrama);
    gl.deleteTexture(this.tImmagine);
    gl.deleteProgram(this.programma);
    gl.deleteVertexArray(this.vao);
    for (const b of this.buffer) gl.deleteBuffer(b);
    this.trama.width = 0;
    this.trama.height = 0;
  }
}
